import storage from "../../utils/storage";
import { store } from "../../App";
import RefreshTokenTimeout from "../../layouts/LoginRequired";

import { Platform } from "react-native";

export const LOG_IN = "LOG_IN";
export const FETCH_ACCOUNT = "FETCH_ACCOUNT";
export const UPDATE_ACCOUNT = "UPDATE_ACCOUNT";
export const LOGIN_ERROR = "LOGIN_ERROR";
export const LOGIN_BAD_AUTH = "LOGIN_BAD_AUTH";
export const CLEAR_ACCOUNT = "CLEAR_ACCOUNT";
export const WAITING_LOG_IN = "WAITING_LOG_IN";
export const REFRESH_ACCESS_TOKEN = "REFRESH_ACCESS_TOKEN";
export const BACKGROUND_REFRESH_ACCESS_TOKEN =
  "BACKGROUND_REFRESH_ACCESS_TOKEN";
export const FETCH_REQUEST_PASSWORD_RESET = "FETCH_REQUEST_PASSWORD_RESET";
export const UPDATE_REQUEST_PASSWORD_RESET = "UPDATE_REQUEST_PASSWORD_RESET";
export const CLEAR_PASSWORD_RESET = "CLEAR_PASSWORD_RESET";
export const FETCH_DISAVOW_PASSWORD_RESET = "FETCH_DISAVOW_PASSWORD_RESET";
export const FETCH_VERIFY_PASSWORD_RESET_TOKEN =
  "FETCH_VERIFY_PASSWORD_RESET_TOKEN";
export const UPDATE_VERIFY_PASSWORD_RESET_TOKEN =
  "UPDATE_VERIFY_PASSWORD_RESET_TOKEN";
export const FETCH_CHANGE_PASSWORD = "FETCH_CHANGE_PASSWORD";
export const UPDATE_CHANGE_PASSWORD = "UPDATE_CHANGE_PASSWORD";
export const CLEAR_CHANGE_PASSWORD = "CLEAR_CHANGE_PASSWORD";

export const accessTokenItemName = "session.accessToken";

export function refreshLogin(accessToken) {
  if (accessToken) {
    try {
      const accessTokenPayload = getAccessTokenPayload(accessToken);
      const now = getNowUnix();

      // The access token we have has not yet expired then we are good to go so just fetch the account
      // and set a timeout to refresh the token later.
      if (now < accessTokenPayload.exp) {
        setAccessTokenRefreshTimeout(accessTokenPayload);
      } else {
        return refreshAccessToken();
      }
    } catch (err) {
      console.error(err);
      return logout();
    }
  } else {
    // Force a logout if there is no token just to make sure all the state
    // is in the appropriate format.
    return logout();
  }
}

export function getAccessTokenPayload(accessToken) {
  const accessTokenParts = accessToken.split(".");
  return JSON.parse(atob(accessTokenParts[1]));
}

export function login(username, password, captchaToken) {
  return {
    type: LOG_IN,
    meta: {
      endpoint: "/auth/login/",
      noAuthorizationHeader: true,
      method: "POST",
      body: {
        username: username,
        password: password,
        captchaToken: captchaToken,
      },
      success: loginSuccess,
      error: loginError,
    },
  };
}

function getNowUnix() {
  const nowDate = new Date();
  return Math.ceil(nowDate.getTime() / 1000); // convert from ms to s
}

function setAccessTokenRefreshTimeout(accessTokenPayload) {
  const now = getNowUnix();
  const expiresIn = accessTokenPayload.exp - now;
  const refreshIn = expiresIn - 300; // Refresh the access token 10 mins before it actually expires.
  // We do not have to worry about the timeout going negative, a negative timeout execute
  // immediately.
  setTimeout(
    () => store.dispatch(backgroundRefreshAccessToken()),
    refreshIn * 1000
  ); // ms to s
}

function updateAccessToken(loginResponse) {
  storage.save({ key: accessTokenItemName, data: loginResponse.access_token });
  if (Platform.OS !== "web") {
    storage.save({ key: "refreshToken", data: loginResponse.refresh_token });
    storage.save({ key: "expiry", data: loginResponse.expires_at });
  }
}

export function loggedIn() {
  return {
    type: UPDATE_ACCOUNT,
  };
}

export function needsLogin() {
  return {
    type: WAITING_LOG_IN,
  };
}

function loginSuccess(loginResponse) {
  storage.save({
    key: accessTokenItemName,
    data: loginResponse["access_token"],
  });
  if (Platform.OS !== "web") {
    storage.save({ key: "refreshToken", data: loginResponse.refresh_token });
    storage.save({ key: "expiry", data: loginResponse.expires_at });
  }
  return fetchAccount();
}

export function fetchAccount() {
  return {
    type: FETCH_ACCOUNT,
    meta: {
      endpoint: "/account",
      success: updateAccount,
      error: loginError,
      errorMessage: "Failed to load user account details",
    },
  };
}

export function updateAccount(fetched) {
  return {
    type: UPDATE_ACCOUNT,
    payload: {
      permissions: fetched.data.permissions,
    },
  };
}

export function logout(rpToken) {
  storage.remove({ key: accessTokenItemName });
  if (Platform.OS !== "web") {
    storage.remove({ key: "refreshToken" });
    storage.remove({ key: "expiry" });
  }
  // We have to explicitly pass the rpToken to this action otherwise when the async fetch happens the storage
  // item will have already been deleted.
  clearTimeout(RefreshTokenTimeout);
  return clearAccount(rpToken);
}

export function backgroundRefreshAccessToken() {
  return {
    type: BACKGROUND_REFRESH_ACCESS_TOKEN,
    meta: {
      endpoint: "/auth/refresh/",
      method: "POST",
      success: updateAccessToken,
    },
  };
}

export function refreshAccessToken() {
  return {
    type: REFRESH_ACCESS_TOKEN,
    meta: {
      endpoint: "/auth/refresh/",
      method: "POST",
      success: loginSuccess,
    },
  };
}

function loginError(payload, response) {
  // If we've used a token and gotten a 401 then the token is no longer valid
  // so ensure we remove it.
  if (response && response.status === 401) {
    storage.remove({ key: accessTokenItemName });
  }
  if (Platform.OS !== "web") {
    storage.remove({ key: "refreshToken" });
    storage.remove({ key: "expiry" });
  }
  return {
    type: LOGIN_ERROR,
  };
}

function clearAccount(rpToken) {
  console.log("clearAccount");
  return {
    type: "RESET_STORE",
    meta: {
      endpoint: "/auth/logout/",
      rpToken: rpToken,
      // No callbacks here as we mark them as logged out immediately anyway.
    },
  };
}

export function requestPasswordReset(emailAddress) {
  return {
    type: FETCH_REQUEST_PASSWORD_RESET,
    meta: {
      endpoint: "/password/reset",
      method: "POST",
      body: {
        email: emailAddress,
      },
      success: updateRequestPasswordReset,
    },
  };
}

function updateRequestPasswordReset(resetResp) {
  return {
    type: UPDATE_REQUEST_PASSWORD_RESET,
    payload: {
      message: resetResp.message,
      status: resetResp.status,
    },
  };
}

export function clearPasswordReset() {
  return {
    type: CLEAR_PASSWORD_RESET,
  };
}

export function disavowPasswordReset(token) {
  return {
    type: FETCH_DISAVOW_PASSWORD_RESET,
    meta: {
      endpoint: "/password/reset/disavow?token=" + token,
      method: "POST",
    },
  };
}

export function verifyPasswordResetToken(token) {
  return {
    type: FETCH_VERIFY_PASSWORD_RESET_TOKEN,
    meta: {
      endpoint: "/password/change/verify_token?token=" + token,
      success: updateVerifyPasswordResetToken,
    },
  };
}

export function updateVerifyPasswordResetToken(token) {
  return {
    type: UPDATE_VERIFY_PASSWORD_RESET_TOKEN,
    payload: {
      status: token.status,
      tokenValid: token.valid,
    },
  };
}

export function changePassword(password, confirmPassword, token) {
  return {
    type: FETCH_CHANGE_PASSWORD,
    meta: {
      endpoint: "/password/change?token=" + token,
      method: "POST",
      body: {
        password: password,
        confirmPassword: confirmPassword,
      },
      success: updateChangePassword,
      error: failedChangePassword,
    },
  };
}

function updateChangePassword(resp) {
  console.log("NOT FAILED");
  return {
    type: UPDATE_CHANGE_PASSWORD,
    payload: {
      status: resp.status,
      message: resp.message,
    },
  };
}

function failedChangePassword(_, resp) {
  console.log(_, resp);
  console.log("FAILED");
  if (resp.status === 400) {
    return {
      type: UPDATE_CHANGE_PASSWORD,
      payload: {
        status: "error",
        message: "Password is too weak",
      },
    };
  } else {
    return {
      type: UPDATE_CHANGE_PASSWORD,
      payload: {
        status: "error",
        message:
          "We were unable to reset your password. Please contact support",
      },
    };
  }
}

export function clearChangePassword() {
  return {
    type: CLEAR_CHANGE_PASSWORD,
  };
}



// WEBPACK FOOTER //
// ./src/redux/actions/user.js